import { CalendarDate } from '@internationalized/date';
import {
  SalesOrderUpdateInput,
  SalesOrderType,
  SalesOrderStatus,
  SalesOrderLineItemInput,
  FreightTerm,
  PaymentTerm,
  AddressInput,
  WeightDisplayUnit,
} from '@rematter/frontend-lib-api-client';

import { createMutationAtomFactory } from '~/shared/providers/api/createMutationAtomFactory';

export type SalesOrderUpdateParams = {
  id: string;
  documentNumber?: string;
  type?: SalesOrderType | null;
  status?: SalesOrderStatus | null;
  startDate?: CalendarDate | null;
  endDate?: CalendarDate | null;
  accountId?: string | null;
  counterpartyReferenceNumber?: string | null;
  contactId?: string | null;
  facilityId?: string | null;
  accountRepresentativeId?: string | null;
  shippingAddress?: AddressInput;
  billingAddress?: AddressInput;
  paymentCurrency?: string | null;
  freightTerm?: FreightTerm | null;
  paymentTerm?: PaymentTerm | null;
  doNotShip?: boolean;
  recyclerExternalNotes?: string | null;
  recyclerInternalNotes?: string | null;
  completionNotes?: string | null;
  lineItems?: SalesOrderLineItemInput[];
  weightDisplayUnit?: WeightDisplayUnit | null;
  bookingIds?: string[];
  forceUnlink?: boolean;
};
export const salesOrderUpdateAtom = createMutationAtomFactory(
  'trade.salesOrderUpdate',
  (params: SalesOrderUpdateParams) => {
    const input: SalesOrderUpdateInput = {};

    // Only add fields that have values
    if (params.documentNumber) input.documentNumber = params.documentNumber;
    if (params.type) input.type = params.type;
    if (params.status) input.status = params.status;
    if (params.startDate) input.startDate = params.startDate.toString();
    if (params.endDate) input.endDate = params.endDate.toString();
    if (params.accountId) input.accountId = params.accountId;
    if (params.counterpartyReferenceNumber)
      input.counterpartyReferenceNumber = params.counterpartyReferenceNumber;
    if (params.contactId) input.contactId = params.contactId;
    if (params.facilityId) input.facilityId = params.facilityId;
    if (params.accountRepresentativeId)
      input.accountRepresentativeId = params.accountRepresentativeId;
    if (params.shippingAddress) input.shippingAddress = params.shippingAddress;
    if (params.billingAddress) input.billingAddress = params.billingAddress;
    if (params.paymentCurrency) input.paymentCurrency = params.paymentCurrency;
    if (params.freightTerm) input.freightTerm = params.freightTerm;
    if (params.paymentTerm) input.paymentTerm = params.paymentTerm;
    if (params.doNotShip !== undefined) input.doNotShip = params.doNotShip;
    if (params.recyclerExternalNotes !== undefined)
      input.recyclerExternalNotes = params.recyclerExternalNotes;
    if (params.recyclerInternalNotes !== undefined)
      input.recyclerInternalNotes = params.recyclerInternalNotes;
    if (params.completionNotes) input.completionNotes = params.completionNotes;
    if (params.lineItems) input.lineItems = params.lineItems;
    if (params.weightDisplayUnit)
      input.weightDisplayUnit = params.weightDisplayUnit;

    if (params.bookingIds !== undefined) input.bookingIds = params.bookingIds;

    return {
      tradeSalesOrderUpdate: {
        __args: {
          id: params.id,
          input,
          forceUnlink: params.forceUnlink ?? false,
        },
        salesOrder: {
          id: true,
          status: true,
          documentNumber: true,
          bookings: {
            id: true,
            documentNumber: true,
          },
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
        },
        blockingLoads: {
          id: true,
          documentNumber: true,
        },
        unlinkableLoads: {
          id: true,
          documentNumber: true,
        },
        removedBookingId: true,
        requiresConfirmation: true,
      },
    };
  },
);
